import dicomParser from 'dicom-parser';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import * as cornerstoneWebImageLoader from 'cornerstone-web-image-loader';
import * as cornerstoneTools from 'cornerstone-tools';
import * as cornerstoneMath from 'cornerstone-math';
import Hammer from 'hammerjs';
import JSZip from 'jszip';
import { parseDicomdir } from '../dicomdirReader';

// Configuração do Cornerstone
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWebImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
cornerstone.registerImageLoader('wadouri', cornerstoneWADOImageLoader.wadoUriLoader);
cornerstone.registerImageLoader('wadors', cornerstoneWADOImageLoader.wadoRsLoader);

// Configuração do CornerstoneTools
cornerstoneTools.external.cornerstone = cornerstone;
cornerstoneTools.external.Hammer = Hammer;
cornerstoneTools.external.cornerstoneMath = cornerstoneMath;

export const loadDicomImage = async (referencedFile, zipFiles, loadedImages, setLoadedImages) => {
  if (loadedImages[referencedFile]) {
    return loadedImages[referencedFile];
  }

  let imageUrl = null;
  try {
    if (!referencedFile || referencedFile === 'N/A') {
      throw new Error('Caminho do arquivo inválido');
    }

    if (!zipFiles) {
      throw new Error('Arquivo ZIP não carregado');
    }

    const normalizedPath = referencedFile.replace(/\\/g, '/');
    const possiblePaths = [
      `exam/${normalizedPath}`,
      `EXAM/${normalizedPath}`,
      `exam\\${referencedFile}`,
      `EXAM\\${referencedFile}`,
    ];

    let imageFile = null;
    for (const path of possiblePaths) {
      imageFile = zipFiles.files[path];
      if (imageFile) break;
    }

    if (!imageFile) {
      throw new Error('Imagem não encontrada. Verifique se o arquivo existe no ZIP.');
    }

    const arrayBuffer = await imageFile.async('arraybuffer');
    const blob = new Blob([arrayBuffer], { type: 'application/dicom' });
    imageUrl = URL.createObjectURL(blob);

    try {
      const image = await cornerstone.loadImage(`wadouri:${imageUrl}`);
      setLoadedImages((prev) => ({ ...prev, [referencedFile]: image }));
      return image;
    } catch (cornerstoneError) {
      throw new Error(`Erro ao processar imagem DICOM: ${cornerstoneError.message}`);
    }
  } catch (error) {
    return null;
  } finally {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
  }
};

export const handleZipFile = async (file, setLoading, setError, setRecords, setZipFiles) => {
  if (!file) return;

  try {
    setLoading(true);
    setError(null);

    const zip = new JSZip();
    const zipContent = await zip.loadAsync(file);
    setZipFiles(zipContent);

    let dicomdirFile = null;
    for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
      if (filename.toUpperCase().endsWith('DICOMDIR')) {
        const arrayBuffer = await zipEntry.async('arraybuffer');
        dicomdirFile = new File([arrayBuffer], filename);
        break;
      }
    }

    if (!dicomdirFile) {
      throw new Error(
        'Arquivo DICOMDIR não encontrado no ZIP. Verifique se o arquivo está na raiz do ZIP ou em uma pasta DICOM/EXAM.'
      );
    }

    const parsedRecords = await parseDicomdir(dicomdirFile);
    setRecords(parsedRecords);
  } catch (err) {
    console.error('Error processing ZIP:', err);
    setError(err.message || 'Erro ao processar o arquivo ZIP');
    setRecords([]);
  } finally {
    setLoading(false);
  }
};

export const organizeRecords = (records) => {
  const organized = {};

  for (const record of records) {
    const seriesId = record.seriesInstanceUID || 'unknown';

    if (!organized[seriesId]) {
      organized[seriesId] = {
        seriesDescription: record.seriesDescription || 'Sem descrição',
        modality: record.modality || 'N/A',
        seriesDate: record.seriesDate || 'Data desconhecida',
        seriesNumber: record.seriesNumber || '',
        patientName: record.patientName || 'Sem nome',
        patientId: record.patientID || 'N/A',
        studyInstanceUID: record.studyInstanceUID || 'N/A',
        studyDescription: record.studyDescription || '',
        studyDate: record.studyDate || '',
        seriesId: seriesId,
        images: [],
      };
    }

    if (record.referencedFile) {
      organized[seriesId].images.push(record);
    }
  }

  return organized;
};

export const loadZipFromUrl = async (
  url,
  setLoading,
  setError,
  setRecords,
  setZipFiles,
  setProgress
) => {
  try {
    setLoading(true);
    setError(null);
    setProgress(0);

    const response = await fetch(url);
    console.log('Response:', response);

    if (!response.ok) {
      throw new Error('Failed to fetch ZIP file');
    }

    const contentLength = response.headers.get('Content-Length');
    const totalSize = contentLength ? Number.parseInt(contentLength, 10) : 0;

    if (!response.body) {
      throw new Error('ReadableStream não está disponível neste ambiente.');
    }

    // Acumula os chunks recebidos
    const reader = response.body.getReader();
    let bytesReceived = 0;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      bytesReceived += value.byteLength;

      if (totalSize > 0) {
        const currentProgress = Math.min((bytesReceived / totalSize) * 100, 100);
        setProgress(currentProgress);
      } else {
        // Se não tivermos o tamanho total, exibimos os bytes recebidos
        setProgress(bytesReceived);
      }
    }

    // Cria o blob a partir dos chunks acumulados
    const blob = new Blob(chunks, { type: 'application/zip' });
    const file = new File([blob], 'downloaded.zip', { type: 'application/zip' });
    
    await handleZipFile(file, setLoading, setError, setRecords, setZipFiles);
  } catch (error) {
    console.error('Erro no loadZipFromUrl:', error);
    setError(error.message);
    setLoading(false);
  }
};

